<script setup lang="ts">
import { MagnifyingGlassIcon } from '@heroicons/vue/24/outline';
import Logo from '~/components/design-system/logo.vue';
import { useSiteStore } from '~/stores/site.store';
import { useAuthStore } from '~/stores/auth.store';
import type { ISitemapRemap } from '~/interfaces/dto/sitemap/response';
import { type UserNavigationBarDropdownToggle } from '#components';
import type {
  IHamburgerMenuState,
  LayoutActiveStates,
} from '~/interfaces/components/layout';

const settings = useSiteStore();
const auth = useAuthStore();
const route = useRoute();
const viewport = useViewport();

const target = ref<HTMLElement>();

const hamburgerMenuState = inject<IHamburgerMenuState>('hamburgerMenuState');
const layoutActiveStates = inject<LayoutActiveStates>('activeStates');

const toggle = () => {
  layoutActiveStates.topNavigation = !layoutActiveStates.topNavigation;
  target.value.scroll({ left: 0 });
};

const navigationLinks = computed(() => {
  if (route.path.startsWith('/blog')) {
    const pageDefinition = useGetPage({
      path: 'blog',
      setSeoMeta: false,
    }).value;
    return pageDefinition?.Children;
  }
  return settings.getNav({ nav: 'top' });
});

const loggedInWithSpin = computed<boolean>(
  () => !!auth.getCurrentSpin && !!auth.isLoggedIn,
);

const isMobile = computed<boolean>(() => viewport.isLessThan('tablet'));

const showSkeletons = computed<boolean>(
  () => settings.getNav({ nav: 'top' })?.length < 1,
);

const isActiveLink = computed<(item: Partial<ISitemapRemap>) => boolean>(
  () => (item: Partial<ISitemapRemap>) => {
    if (item?.isHome) return route.path === '/';
    else return route.fullPath.startsWith(item.Path);
  },
);

const scrollNav = (alias: string) => {
  if (isMobile.value) {
    const navItem = document?.getElementById(`${alias}-nav-item`);
    setTimeout(() => {
      navItem?.scrollIntoView({
        behavior: 'smooth',
        inline: 'center',
        block: 'nearest',
      });
    }, 350);
  }
};

const toggleHamburger = () => {
  hamburgerMenuState.drawerTransition = {
    enterFromClass: '-translate-x-full',
    enterToClass: 'translate-x-0',
    leaveFromClass: 'translate-x-0',
    leaveToClass: '-translate-x-full',
  };
  hamburgerMenuState.active = !hamburgerMenuState?.active;
};

watch(
  () => [route.path, isMobile.value],
  () => {
    if (layoutActiveStates.topNavigation) toggle();
    if (isMobile.value)
      nextTick(() =>
        scrollNav(
          navigationLinks?.value?.find((navLink) =>
            isActiveLink?.value(navLink),
          )?.Alias,
        ),
      );
  },
);
</script>
<template>
  <header
    id="site-header"
    class="sticky top-0 bg-layer z-[15] w-full shadow-lg dark:shadow-2xl"
    :class="{
      fixed: layoutActiveStates.topNavigation,
    }"
  >
    <div
      class="container mx-auto flex justify-between items-center !py-1.5 px-2"
    >
      <div class="flex items-center max-w-fit overflow-hidden">
        <UserInterfaceHamburgerMenu
          v-model:visible="hamburgerMenuState.active"
          @activator-click="toggleHamburger"
        />
        <NuxtLink to="/">
          <Logo
            element-name="platform-logo"
            image-class="cursor-pointer py-1 md:py-2 site-logo h-8 md:h-12 xl:h-14"
          />
        </NuxtLink>
      </div>
      <UserActionsNavBar />
    </div>
    <hr class="border-t-[1px] border-light-200 dark:border-dark-900 w-full" />
    <div
      class="md:container md:mx-auto relative flex items-start justify-start"
      :class="{ 'items-center': layoutActiveStates.topNavigation }"
    >
      <nav
        ref="target"
        class="relative w-full transition-all duration-300 ease-in-out h-[55px]"
        :class="{ 'flex flex-1': !layoutActiveStates.topNavigation }"
      >
        <div
          class="bg-light-50 dark:bg-dark-800 relative"
          :class="{
            'overflow-x-scroll flex items-center no-scrollbar w-dvw h-[55px] pr-2':
              !layoutActiveStates.topNavigation,
            'w-full fixed top-0 left-0 z-5 flex flex-wrap p-2 md:p-0 h-auto pr-10 transition-all duration-300 ease-in-out':
              layoutActiveStates.topNavigation,
          }"
        >
          <div
            class="md:hidden h-full w-fit px-3 py-2 text-base text-base-color flex items-center cursor-pointer justify-center sticky left-0 bg-light-50 dark:bg-dark-800 z-2"
            :class="{ hidden: layoutActiveStates.topNavigation }"
            @click="settings.activateModal('search')"
          >
            <MagnifyingGlassIcon class="w-5 stroke-3" />
          </div>
          <UserInterfaceVerticalNavigationButton
            v-for="item in navigationLinks"
            :key="item.Alias"
            :is-active="isActiveLink(item)"
            :drop-down-active="layoutActiveStates.topNavigation"
            :navigation-link="item"
            :binding-class="[
              'w-1/3 md:w-fit flex items-center justify-center',
              {
                'xxsm:w-1/4 xsm:w-1/7 sm:w-[15%]':
                  layoutActiveStates.topNavigation,
              },
            ]"
            @navigate="
              (scrollNav(item.Alias),
              layoutActiveStates.topNavigation ? toggle() : null)
            "
          />
          <LazyUserInterfaceSpinNowVerticalNavigationButton
            v-if="loggedInWithSpin"
            :is-active="
              isActiveLink({
                Path: auth.getCurrentSpin.redirectUrl,
              } as Partial<ISitemapRemap>)
            "
            :redirect-path="auth.getCurrentSpin.redirectUrl"
            :spin-count="auth.getCurrentSpin?.unusedSpinCount"
          />
          <div
            v-show="showSkeletons"
            class="flex items-center gap-1 h-full w-full px-2"
          >
            <UserInterfaceGenericSkeleton
              v-for="n in 6"
              :key="n"
              width-class="w-14"
              height-class="h-full"
            />
          </div>
        </div>
        <UserNavigationBarDropdownToggle
          v-show="!showSkeletons"
          :open="layoutActiveStates.topNavigation"
          @toggle="toggle()"
        />
      </nav>
    </div>
  </header>
</template>
<style scoped lang="scss"></style>
